import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthApiError } from "@/lib/auth";
import {
  confirmPasswordReset,
  lookupPasswordReset,
} from "@/lib/auth/password-reset-api";
import { cn } from "@/lib/utils";

// Per spec ("错误提示统一用 'Link invalid or expired' 不要细分"), any token
// failure — unknown, used, expired — collapses to the same opaque message.
const LINK_INVALID_OR_EXPIRED = "Link invalid or expired";
const SUCCESS_REDIRECT_MS = 3000;

interface PasswordChecks {
  length: boolean;
  letter: boolean;
  digit: boolean;
}

function checkPassword(value: string): PasswordChecks {
  return {
    length: value.length >= 10,
    letter: /[A-Za-z]/.test(value),
    digit: /[0-9]/.test(value),
  };
}

function isPasswordValid(checks: PasswordChecks): boolean {
  return checks.length && checks.letter && checks.digit;
}

type Stage = "verifying" | "invalid" | "form" | "done";

export function PasswordResetConfirmPage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("verifying");
  const [email, setEmail] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const passwordChecks = useMemo(() => checkPassword(password), [password]);
  const passwordOk = isPasswordValid(passwordChecks);
  const confirmMismatch = confirm.length > 0 && confirm !== password;

  useEffect(() => {
    if (!token) {
      setStage("invalid");
      setVerifyError(LINK_INVALID_OR_EXPIRED);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await lookupPasswordReset(token);
        if (cancelled) return;
        setEmail(info.email);
        setStage("form");
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AuthApiError) {
          setVerifyError(LINK_INVALID_OR_EXPIRED);
        } else {
          setVerifyError(
            "Couldn't reach the server. Check your connection and retry.",
          );
        }
        setStage("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (stage !== "done") return;
    const id = window.setTimeout(() => {
      navigate("/login", { replace: true });
    }, SUCCESS_REDIRECT_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [stage, navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    if (!passwordOk) {
      setFormError(
        "Please choose a password that meets all the requirements.",
      );
      return;
    }
    if (confirm !== password) {
      setFormError("Passwords don't match.");
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset({ token, password });
      setStage("done");
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === "invalid_or_expired") {
          setStage("invalid");
          setVerifyError(LINK_INVALID_OR_EXPIRED);
          return;
        }
        if (err.code === "validation_failed") {
          setFormError(
            "Please choose a password that meets all the requirements.",
          );
          return;
        }
        setFormError(
          err.serverMessage ?? "Couldn't reset password. Please try again.",
        );
      } else {
        setFormError(
          "Couldn't reach the server. Check your connection and retry.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            Finance
          </p>
          <h1 className="text-xl font-semibold tracking-tight">
            {stage === "done"
              ? "Password updated"
              : "Choose a new password"}
          </h1>
        </div>

        {stage === "verifying" ? <VerifyingCard /> : null}

        {stage === "invalid" ? (
          <InvalidCard message={verifyError ?? LINK_INVALID_OR_EXPIRED} />
        ) : null}

        {stage === "form" && email ? (
          <FormCard
            email={email}
            password={password}
            confirm={confirm}
            checks={passwordChecks}
            passwordOk={passwordOk}
            confirmMismatch={confirmMismatch}
            error={formError}
            submitting={submitting}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirm}
            onSubmit={handleSubmit}
          />
        ) : null}

        {stage === "done" ? <DoneCard /> : null}
      </div>
    </div>
  );
}

function VerifyingCard() {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Checking your reset link…
        </div>
      </CardContent>
    </Card>
  );
}

function InvalidCard({ message }: { message: string }) {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm normal-case tracking-normal text-foreground">
          Reset link
        </CardTitle>
        <CardDescription>
          This link can't be used to reset your password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          {message}
        </p>
        <Button asChild className="w-full">
          <Link to="/password-reset/request">Request a new reset link</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function DoneCard() {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm normal-case tracking-normal text-foreground">
          All set
        </CardTitle>
        <CardDescription>
          Your password has been updated. Please sign in again.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p
          role="status"
          className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-foreground"
        >
          Redirecting to sign in…
        </p>
        <Button asChild variant="outline" className="w-full">
          <Link to="/login">Go to sign in now</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function FormCard(props: {
  email: string;
  password: string;
  confirm: string;
  checks: PasswordChecks;
  passwordOk: boolean;
  confirmMismatch: boolean;
  error: string | null;
  submitting: boolean;
  onPasswordChange: (v: string) => void;
  onConfirmChange: (v: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const {
    email,
    password,
    confirm,
    checks,
    passwordOk,
    confirmMismatch,
    error,
    submitting,
    onPasswordChange,
    onConfirmChange,
    onSubmit,
  } = props;
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm normal-case tracking-normal text-foreground">
          Set a new password
        </CardTitle>
        <CardDescription>
          Setting a new password for{" "}
          <span className="font-mono text-foreground">{email}</span>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Hidden username field helps password managers associate the
              new credential with the right account. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            hidden
          />

          <div className="space-y-1.5">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              disabled={submitting}
              aria-describedby="password-rules"
              autoFocus
            />
            <ul
              id="password-rules"
              className="space-y-1 text-xs text-muted-foreground"
            >
              <PasswordRule met={checks.length} label="At least 10 characters" />
              <PasswordRule met={checks.letter} label="Contains a letter" />
              <PasswordRule met={checks.digit} label="Contains a number" />
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => onConfirmChange(e.target.value)}
              disabled={submitting}
              aria-invalid={confirmMismatch || undefined}
            />
            {confirmMismatch ? (
              <p className="text-xs text-destructive">Passwords don't match.</p>
            ) : null}
          </div>

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            disabled={submitting || !passwordOk || confirm !== password}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating password…
              </>
            ) : (
              "Update password"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordRule({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={cn(
          "flex h-3.5 w-3.5 items-center justify-center rounded-full border",
          met
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-background",
        )}
        aria-hidden
      >
        {met ? <Check className="h-2.5 w-2.5" /> : null}
      </span>
      <span className={cn(met ? "text-foreground" : "text-muted-foreground")}>
        {label}
      </span>
    </li>
  );
}
